/**
 * The wording of stage 2, in one place: closing sentences, attachment notes, the price
 * line, margin and item status, order check verdicts, customer roles and line decisions.
 *
 * Pure functions over the wire values, so every sentence a sales person reads can be tested.
 * Dates print as "07 Oct 2026"; a date without a time is read as a calendar day.
 */

import type {
  AttachmentDocType,
  AttachmentNoteCode,
  ClosedBy,
  CustomerRole,
  ItemStatus,
  LineDecision,
  LineSource,
  MarginStatus,
  OrderCheckLine,
  OrderVerdict,
  PhotoKind,
  PhotoReadStatus,
  PhotoValueUse,
  PriceStatus,
} from "@/lib/api";
import { formatDate } from "@/lib/format";
import type { QuoteCaseDetail } from "@/lib/mock";
import {
  customerConfirmedWord,
  LEAD_GAP_WORDS,
  LEAD_ORIGIN_WORDS,
  PICTURE_WORDS,
  PRICE_GAP_WORDS,
  type PriceGap,
} from "@/lib/sales-flow";

// ─── dates and numbers ──────────────────────────────────────────────────────

const DAY_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** "2026-10-07" or an ISO timestamp as "07 Oct 2026"; anything unreadable passes through. */
export function formatDay(value: string | null | undefined): string {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    // A calendar day must not shift with the reader's time zone.
    ...(DAY_ONLY.test(value) ? { timeZone: "UTC" } : {}),
  });
}

/** An ISO timestamp as "08 Oct 2026, 14:05" (reader's time zone); unreadable passes through. */
export function formatDayTime(value: string | null | undefined): string {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  const time = date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  return `${formatDay(value)}, ${time}`;
}

/** A price or a ratio as printed: two to four decimals. */
export function formatAmount(value: number | string | null | undefined): string {
  if (value == null || value === "") {
    return "";
  }
  const number = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(number)) {
    return String(value);
  }
  return number.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 4 });
}

/** A money amount as printed: exactly two decimals, thousands separator, then the currency. */
export function formatMoney(
  value: number | string | null | undefined,
  currency?: string | null,
): string {
  if (value == null || value === "") {
    return "";
  }
  const number = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(number)) {
    return "";
  }
  const amount = number.toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const code = (currency ?? "").trim();
  return code ? `${amount} ${code}` : amount;
}

/** "Won value" with its currency; says so when the currency is not known. */
export function wonValueText(amount: number, currency: string | null | undefined): string {
  const code = currency?.trim();
  return code ? formatMoney(amount, code) : `${formatMoney(amount)} (currency not known)`;
}

/** A ratio on 0..1 as a whole percentage with a space: 0.58 → "58 %". Empty when unreadable. */
export function formatRatioPercent(value: number | string | null | undefined): string {
  if (value == null || value === "") {
    return "";
  }
  const number = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(number)) {
    return "";
  }
  return `${Math.round(number * 100)} %`;
}

const PRICE_CHIP_LABELS: Record<string, string> = {
  "price-found": "List price",
  "no-price": "No price",
  "below-margin": "List price, needs margin approval",
  "expired-price": "Price expired",
  "carriage-missing": "Carriage not priced",
};

/** The price chip of I2Q in words; an unknown chip reads "Price", never the code. */
export function priceChipLabel(chip: string | null | undefined): string {
  return (
    (chip && Object.hasOwn(PRICE_CHIP_LABELS, chip) ? PRICE_CHIP_LABELS[chip] : null) ?? "Price"
  );
}

/**
 * The evidence line of a price a person set: "Price set by a person: 123.45 USD". It sits
 * next to what I2Q found, so a typed price is never read as a list price (tester round 5,
 * 12 Oct 2026).
 */
export function manualPriceEvidenceText(
  unitPrice: number | string | null | undefined,
  currency: string | null | undefined,
): string {
  const amount = formatMoney(unitPrice, currency);
  return amount ? `Price set by a person: ${amount}` : "Price set by a person";
}

/** "List price: 1,234.50 EUR", or the chip words alone when no amount was sent. */
export function priceEvidenceText(
  chip: string | null | undefined,
  unitPrice: number | string | null | undefined,
  currency: string | null | undefined,
): string {
  const label = priceChipLabel(chip);
  const amount = formatMoney(unitPrice, currency);
  return amount ? `${label}: ${amount}` : label;
}

/** A quantity as printed: no forced decimals. */
export function formatQty(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) {
    return "";
  }
  return value.toLocaleString("en-GB", { maximumFractionDigits: 3 });
}

// ─── closing ────────────────────────────────────────────────────────────────

/** Why a case counts as answered: "Quotation SQ0990001 sent on 07 Oct 2026". */
export function closedBySentence(closed: ClosedBy | null | undefined): string | null {
  if (!closed) {
    return null;
  }
  return closedKindSentence(closed.kind, closed.at, closed.doc_number);
}

/** The same sentence from the queue row, which carries no document number. */
export function closedKindSentence(
  kind: ClosedBy["kind"] | null | undefined,
  at: string | null | undefined,
  docNumber?: string | null,
): string | null {
  const on = at ? ` on ${formatDay(at)}` : "";
  const number = docNumber ? ` ${docNumber}` : "";
  switch (kind) {
    case "quotation_sent":
      return `Quotation${number} sent${on}`;
    case "order_confirmation_sent":
      return `Order confirmation${number} sent${on}`;
    case "rep_reply":
      return `Reply sent${on}`;
    case "fo_quotation":
      // Only while the switch `autonomy_replied_from_fo` is on (OFF by default).
      return `Quotation${number} found in F&O for this sender${on}`;
    // The shadow auto-close (11 Oct 2026), only while its switch is on: an I2Q status,
    // nothing was moved or sent in any mailbox.
    case "desk_done":
      return `Moved to the done folder of the shared mailbox${on}`;
    case "desk_deleted":
      return `Deleted from the shared mailbox${on}`;
    case "own_reply":
      return "A Norican reply, not an inquiry";
    case "thread_reply":
      return `Reply sent in the mail thread${on}`;
    case "thread_head_closed":
      return "Follows a closed inquiry";
    default:
      return null;
  }
}

/**
 * Who closed a case and when, for the Closed tab (360 simulation P24). The date is when
 * sales set the status or when I2Q decided the close, never the time of the evidence
 * mail. Null for a case that is not closed or when the server did not send the time.
 */
export function closedOnText(row: {
  workStatus?: string | null;
  closedDecidedAt?: string | null;
}): string | null {
  if (!row.closedDecidedAt) {
    return null;
  }
  const day = formatDay(row.closedDecidedAt);
  if (!day) {
    return null;
  }
  if (row.workStatus === "done" || row.workStatus === "not_for_us") {
    return `Closed by sales on ${day}`;
  }
  if (row.workStatus === "auto_closed" || row.workStatus === "no_activity") {
    return `Closed by I2Q on ${day}`;
  }
  return null;
}

/** The sub-filters of the Closed tab with their chip labels (P24). */
export const CLOSED_FILTER_LABELS = {
  no_reply_seen: "Closed without a reply seen",
  today: "Closed today",
} as const;

// ─── attachments ────────────────────────────────────────────────────────────

const NOTE_LABELS: Record<AttachmentNoteCode, string> = {
  scan: "Scan without text, not read",
  unreadable_text: "I2Q cannot read the text of this PDF",
  encrypted: "Password protected, not read",
  too_large: "Larger than 15 MB, not read",
  too_many_pages: "More than 30 pages, not read",
  pages_cut: "Long document: I2Q read the first 8 pages only",
  format_not_supported: "I2Q cannot read this file type. Open the file.",
  not_an_order_document: "Not an order or inquiry document",
  no_lines: "No item lines found in the document",
  unreadable: "I2Q cannot read this file",
  budget: "Not read: this mail reached its document limit or its time limit",
  // 14 Oct 2026: the daily cap of the pictures (`RFQ_FINOPS_VISION_DAY_EUR`) is reached.
  daily_limit: "Not read: the daily limit for reading pictures is reached",
  lines_cut: "Long document: I2Q read the first lines only",
  model_rejected: "I2Q did not read this file. Open the file.",
  duplicate_document: "Same document as another attachment",
  image_unclear: "I2Q read the scan twice with different results. Nothing is kept.",
  decoration: "Logo, banner or signature picture, not read",
  small_image: "Small picture, not read",
  seen_in_thread: "Same picture as in an earlier mail of this thread",
  check_missing: "Read once only: the second reading failed",
  heic_not_supported: "HEIC photo, not read. Ask the customer for a JPEG",
  // Hunt round 2, decision 1 (CONTRACTS.md 3.5).
  no_request: PICTURE_WORDS.noRequest,
  holds_pictures: PICTURE_WORDS.holdsPictures,
  // Decision D2 (19 Oct 2026): read on the case of the quoted mail, not again on the reply.
  quoted_picture: PICTURE_WORDS.quotedPicture,
};

/** The note printed for an attachment, or null without a note (or for an unknown code). */
export function attachmentNoteLabel(note: string | null | undefined): string | null {
  return note && Object.hasOwn(NOTE_LABELS, note) ? NOTE_LABELS[note as AttachmentNoteCode] : null;
}

const DOC_TYPE_LABELS: Record<AttachmentDocType, string> = {
  purchase_order: "Purchase order",
  request_for_quotation: "Request for quotation",
  our_quotation: "Our quotation",
  our_order_confirmation: "Our order confirmation",
  // ste-allow: product-term (a parts list is a domain noun, like spare parts)
  parts_list: "Parts list",
  invoice: "Invoice",
  terms: "Terms and conditions",
  drawing: "Drawing",
  service_report: "Service report",
  other: "Other document",
};

export function docTypeLabel(docType: string | null | undefined): string | null {
  return docType && Object.hasOwn(DOC_TYPE_LABELS, docType)
    ? DOC_TYPE_LABELS[docType as AttachmentDocType]
    : null;
}

/** Printed under a file that was read from page images (stage 3). */
export const IMAGE_READ_SENTENCE =
  "Read from a scan. Check the quantities against the page. I2Q does not read handwritten changes.";

/** Printed when handwriting was seen on a scanned page. */
export const HANDWRITING_SENTENCE = "Handwritten notes on this page.";

/** Printed for a document whose text was decoded from a print file without a text layer. */
export const TYPE3_SENTENCE = "Text recovered from the print file.";

/** The sentence for how a file was read; null for the ordinary ways. */
export function readMethodSentence(method: string | null | undefined): string | null {
  if (method === "image") {
    return IMAGE_READ_SENTENCE;
  }
  return method === "type3" ? TYPE3_SENTENCE : null;
}

/** "I2Q did not keep {n} lines because the two readings differed"; null for none. */
export function linesDroppedSentence(count: number | null | undefined): string | null {
  if (typeof count !== "number" || !Number.isFinite(count) || count <= 0) {
    return null;
  }
  const n = Math.floor(count);
  return n === 1
    ? "I2Q did not keep 1 line because the two readings differed"
    : `I2Q did not keep ${n} lines because the two readings differed`;
}

/** "part 2 of 4", "part 2"; null without a part. */
export function partText(part: number | null | undefined, parts?: number | null): string | null {
  if (typeof part !== "number" || !Number.isInteger(part) || part < 1) {
    return null;
  }
  // The printed text is "part 2 of 4": a digit follows, which the glossary rule allows, and a
  // photo cut into parts has no other word (ste-allow below marks the templates).
  return typeof parts === "number" && Number.isInteger(parts) && parts >= part
    ? // ste-allow: ste-wrong
      `part ${part} of ${parts}`
    : // ste-allow: ste-wrong
      `part ${part}`;
}

/**
 * "po-4711.pdf, page 2", "parts.xlsx, sheet Order" or "IMG_0412.jpg, part 2 of 4";
 * null for a line from the mail text.
 */
export function lineSourceText(source: LineSource | null | undefined): string | null {
  if (!source || source.kind !== "attachment" || !source.file) {
    return null;
  }
  // Hunt round 2, decision 1: a picture in the mail body, a file inside an attached file.
  const file = attachmentFileText({
    file: source.file,
    inline: source.inline,
    child: source.child,
  });
  if (source.sheet) {
    return `${file}, sheet ${source.sheet}`;
  }
  const part = partText(source.part, source.parts);
  return [file, source.page != null ? `page ${source.page}` : null, part]
    .filter(Boolean)
    .join(", ");
}

/**
 * The name of a read file as the page shows it (hunt round 2, decision 1): "Picture in the
 * mail body" for an inline picture, "In order.eml: page1.jpg" for a file inside an attached
 * mail or Office file (the reader names it "<parent> > <file>"), else the file name.
 */
/** The name of the entry for files inside an attachment (`_INSIDE_SUFFIX` in attach_reader.py). */
const FILES_INSIDE_SUFFIX = " > files inside";

export function attachmentFileText(entry: {
  file: string;
  inline?: boolean | null;
  child?: number | null;
}): string {
  if (entry.inline) {
    return PICTURE_WORDS.inline;
  }
  const at = entry.file.indexOf(" > ");
  if (entry.child != null && at > 0) {
    return PICTURE_WORDS.child(entry.file.slice(0, at).trim(), entry.file.slice(at + 3).trim());
  }
  // Review of round 3, L1: the files inside an attached file could not be listed.
  if (entry.child == null && entry.file.endsWith(FILES_INSIDE_SUFFIX)) {
    const parent = entry.file.slice(0, -FILES_INSIDE_SUFFIX.length).trim();
    if (parent) {
      return PICTURE_WORDS.filesInside(parent);
    }
  }
  return entry.file;
}

// ─── photos and scanned pages read by the model (docs/vision-ocr.md §4) ──────

/** The tag of a line read from a photo (a line from a scanned PDF says "From a scan"). */
export const PHOTO_LINE_TAG = "Read from a photo";

/** Under every reading: what was read is evidence, never a decision. */
export const PHOTO_FOOTER =
  "I2Q read this from the picture. I2Q accepts nothing from a picture without your decision.";

/** Instead of a preview when the picture shows a person. */
export const PHOTO_PEOPLE_SENTENCE =
  "The picture shows people, so there is no preview. Open the file.";

/** The picture carries text that reads like instructions (the values are still evidence). */
export const PHOTO_INJECTION_SENTENCE =
  "This picture contains text that looks like instructions to a machine. I2Q shows its values as evidence only. Check them against the picture.";

/** The content filter refused the picture because of instruction-like text. */
export const PHOTO_FILTER_INSTRUCTIONS_SENTENCE =
  "This picture contains text that looks like instructions to a machine. I2Q did not read it.";

/** The content filter refused the picture for another reason. */
export const PHOTO_REFUSED_SENTENCE = "I2Q refused to read this picture.";

export const PHOTO_HEIC_SENTENCE = "I2Q cannot read HEIC photos yet. Ask the customer for a JPEG.";

export const PHOTO_SEEN_IN_THREAD_SENTENCE =
  "This picture was already in an earlier mail of this thread. I2Q shows the earlier reading.";

export const PHOTO_CHECK_MISSING_SENTENCE =
  "The second reading failed, so no value counts as agreed. Compare each value with the picture.";

export const PHOTO_BUDGET_SENTENCE =
  "Not read: this mail reached its picture limit or its time limit.";

/** A photo of an item without a legible number. */
export const PHOTO_NO_NUMBER_SENTENCE = "Photo of an item, no readable number.";

/** When the preview cannot be shown (the download link stays). */
export const PHOTO_NO_PREVIEW_SENTENCE = "No preview. Open the file.";

/** Under the cut-out shown in "Show where". */
export const PHOTO_FRAME_SENTENCE =
  "The dashed frame shows the approximate place. The cut-out itself is exact.";

export const PHOTO_COMPARE_SENTENCE = "Compare the value with the picture before you decide.";

/** "Gave line 2.", "Gave lines 2, 3 and 5.", "Gave no line." */
export function fedLinesSentence(lines: readonly number[] | null | undefined): string {
  const list = (lines ?? []).filter((n) => Number.isInteger(n) && n >= 1);
  if (list.length === 0) {
    return "Gave no line.";
  }
  if (list.length === 1) {
    return `Gave line ${list[0]}.`;
  }
  return `Gave lines ${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}.`;
}

const PHOTO_KIND_LABELS: Record<PhotoKind, string> = {
  nameplate: "Nameplate",
  part_photo: "Item photo",
  label: "Item label",
  handwritten_list: "Handwritten list",
  scanned_form: "Scanned form",
  drawing: "Drawing",
  screenshot: "Screenshot",
  other: "Other picture",
};

/** "Nameplate", "Marked parts list", and so on; a list with ticked rows says so. */
export function photoKindLabel(kind: PhotoKind, anyMarked = false): string {
  if (anyMarked && (kind === "handwritten_list" || kind === "scanned_form")) {
    // ste-allow: product-term (a parts list is a domain noun, like spare parts)
    return "Marked parts list";
  }
  return Object.hasOwn(PHOTO_KIND_LABELS, kind) ? PHOTO_KIND_LABELS[kind] : "Other picture";
}

const PHOTO_STATUS_LABELS: Record<PhotoReadStatus, string> = {
  agreed: "Agreed (two readings)",
  read_once: "Read once",
  unclear: "Unclear",
};

/** No reading is ever shown as certain: even "agreed" is information, not success. */
const PHOTO_STATUS_TONES: Record<PhotoReadStatus, "info" | "warning" | "danger"> = {
  agreed: "info",
  read_once: "warning",
  unclear: "danger",
};

export function photoStatusTag(status: PhotoReadStatus): {
  label: string;
  tone: "info" | "warning" | "danger";
} {
  return { label: PHOTO_STATUS_LABELS[status], tone: PHOTO_STATUS_TONES[status] };
}

/** "80% sure"; null for a value that is not a number from 0 to 1. */
export function photoConfidenceText(confidence: number | null | undefined): string | null {
  if (typeof confidence !== "number" || !Number.isFinite(confidence)) {
    return null;
  }
  if (confidence < 0 || confidence > 1) {
    return null;
  }
  return `${Math.round(confidence * 100)}% sure`;
}

/** "I2Q did not find 3 values again in the text of the picture and left them out."; null for none. */
export function photoDroppedSentence(count: number | null | undefined): string | null {
  if (typeof count !== "number" || !Number.isFinite(count) || count < 1) {
    return null;
  }
  const n = Math.floor(count);
  return n === 1
    ? "I2Q did not find 1 value again in the text of the picture and left it out."
    : `I2Q did not find ${n} values again in the text of the picture and left them out.`;
}

/** "The two readings differ on 2 values."; null for none. */
export function photoDisagreementSentence(count: number | null | undefined): string | null {
  if (typeof count !== "number" || !Number.isFinite(count) || count < 1) {
    return null;
  }
  const n = Math.floor(count);
  return n === 1
    ? "The two readings differ on 1 value."
    : `The two readings differ on ${n} values.`;
}

/** The "What" column of the panel. */
export const PHOTO_FIELD_LABELS = {
  // ste-allow: customer-text (the number printed on the customer's own part)
  code: "Part number",
  qty: "Quantity",
  description: "Description",
  make: "Make",
  type: "Machine type",
  serial: "Serial number",
  year: "Year",
  drawing_no: "Drawing no.",
  customer_ref: "Customer reference",
  po: "Purchase order no.",
  sales_order: "Our sales order no.",
} as const;

export const PHOTO_MACHINE_USE = "Machine details (I2Q does not use them for matching yet)";
export const PHOTO_REFERENCE_USE = "Shown only";
export const PHOTO_SALES_ORDER_USE = "Shown only. I2Q never uses it to confirm the customer";

/**
 * The "Used for" column of a value read from a picture. `lineText` is what the line
 * shows ("0990041 NOZZLE 12 MM"), when known.
 */
export function photoUsedForText(
  use: PhotoValueUse | null | undefined,
  line: number | null | undefined,
  status: PhotoReadStatus,
  lineText?: string | null,
): string {
  const label = typeof line === "number" && line >= 1 ? `Line ${line}` : null;
  switch (use) {
    case "suggestion":
      return label ? `${label}: suggestion${lineText ? ` ${lineText}` : ""}` : "Suggestion";
    case "candidates":
      return label ? `${label}: candidates to pick from` : "Candidates to pick from";
    case "needs_pick":
      return label ? `${label}: needs your pick` : "Needs your pick";
    case "not_looked_up":
      return "Not looked up";
    case "mail_line":
      return label ? `Also in the mail text, ${label} uses the mail` : "Also in the mail text";
    case "reference":
      return "Reference only, no line";
    case "not_asked":
      return PICTURE_WORDS.notAsked;
    default:
      if (label) {
        return label;
      }
      return status === "unclear" ? "Not looked up" : "No line";
  }
}

/**
 * The reading state on the header of a picture: "Read", "Partly read" or "Not read: ...".
 * `partly` is set by the caller when a value is unclear, dropped or read differently.
 */
export function photoStateTag(facts: {
  hasReading: boolean;
  partly: boolean;
  note?: string | null;
  file?: string | null;
}): { label: string; tone: "neutral" | "warning" } {
  if (facts.hasReading) {
    return facts.partly
      ? { label: "Partly read", tone: "warning" }
      : { label: "Read", tone: "neutral" };
  }
  switch (facts.note) {
    case "format_not_supported":
      return {
        label: /\.(heic|heif)$/i.test(facts.file ?? "")
          ? "Not read: HEIC format"
          : "Not read: file type",
        tone: "neutral",
      };
    case "heic_not_supported":
      return { label: "Not read: HEIC format", tone: "neutral" };
    case "model_rejected":
      return { label: "Not read: I2Q refused it", tone: "warning" };
    case "budget":
      return { label: "Not read: limit of this mail", tone: "neutral" };
    case "daily_limit":
      return { label: "Not read: daily limit", tone: "neutral" };
    case "too_large":
      return { label: "Not read: too large", tone: "neutral" };
    case "quoted_picture":
      return { label: PICTURE_WORDS.quotedPicture, tone: "neutral" };
    default:
      return { label: "Not read", tone: "neutral" };
  }
}

// ─── price, lead time, margin ───────────────────────────────────────────────

export interface PriceFacts {
  status: PriceStatus | null | undefined;
  unitPrice?: string | number | null;
  currency?: string | null;
  unitQty?: number | null;
  uom?: string | null;
  group?: string | null;
  fromQty?: number | null;
  validTo?: string | null;
  expiredOn?: string | null;
  /** A customer is suggested but not confirmed: the price waits for the confirmation. */
  customerSuggested?: boolean;
  /** "sales" for a price a person set (round 4): it is a price, not a list price. */
  source?: "engine" | "sales" | null;
}

/** The one price sentence of a line; null when nothing is to be said (`no_item`, no status). */
export function priceSentence(price: PriceFacts): string | null {
  switch (price.status) {
    case "found": {
      const amount = [formatAmount(price.unitPrice), price.currency].filter(Boolean).join(" ");
      const per = [price.unitQty ?? 1, price.uom].filter((part) => part != null && part !== "");
      const label = price.source === "sales" ? "Price" : "List price";
      let text = `${label} ${amount} per ${per.join(" ")}`;
      if (price.group) {
        text += ` (${price.group})`;
      }
      if (price.validTo) {
        text += `, valid to ${formatDay(price.validTo)}`;
      }
      if (price.fromQty != null && price.fromQty > 1) {
        // A quantity break: the price applies from this many units on.
        const unit = price.uom || "pcs";
        text += `, from ${formatQty(price.fromQty)} ${unit}`;
      }
      return text;
    }
    case "no_customer":
      return price.customerSuggested
        ? PRICE_GAP_WORDS.no_customer
        : "No price: the customer is not identified";
    case "no_quantity":
      return PRICE_GAP_WORDS.no_quantity;
    case "no_price_group":
      return "No price: the customer has no price group in F&O";
    case "no_price":
      return "No price in our price data for this item";
    case "expired":
      return price.expiredOn
        ? `No valid price in our price data (the last price expired ${formatDay(price.expiredOn)})`
        : "No valid price in our price data";
    case "currency_ambiguous":
      return "No price: several currencies on file";
    case "other_entity":
      return "No price: the item is in another catalogue";
    case "not_loaded":
      return "Prices are not loaded yet";
    case "unavailable":
      return "Price lookup is not available now";
    default:
      return null;
  }
}

/**
 * The gap of a price status, for the words of the price cell (sales review of 12 Oct
 * 2026, P-01). The customer first (`no_customer`: to confirm, or to find when the
 * caller says nobody is suggested, `customerToFind`), the quantity first (`no_quantity`), an item of another
 * catalogue (`other_entity`), no item on the line (`no_item`: nothing will be looked up),
 * F&O holds no price (`no_price`, `no_price_group`), its price expired (`expired`), it
 * holds several currencies (`currency_ambiguous`), else no lookup ran (`not_loaded`,
 * `unavailable`, no status at all). One gap per long sentence of {@link priceSentence},
 * so the cell and the sentence never say two things.
 */
export function priceGapOf(
  status: PriceStatus | string | null | undefined,
  customerToFind = false,
): PriceGap {
  switch (status) {
    case "no_customer":
      return customerToFind ? "no_customer_open" : "no_customer";
    case "no_quantity":
    case "other_entity":
    case "no_item":
    case "expired":
    case "currency_ambiguous":
      return status;
    case "no_price":
    case "no_price_group":
      return "no_price";
    default:
      return "not_looked_up";
  }
}

/** The words of the price cell for a status without a price (P-01), never a bare "no price". */
export function priceGapWords(
  status: PriceStatus | string | null | undefined,
  customerToFind = false,
): string {
  return PRICE_GAP_WORDS[priceGapOf(status, customerToFind)];
}

/** Where a lead time comes from, as the line block reads it. */
export interface LeadTimeSourceFacts {
  kind:
    | "trade_agreement"
    | "purchase"
    | "last_quoted"
    | "mail_offer"
    | "item_master_sales"
    | "item_master_purchase"
    | "item_master_inventory";
  text?: string | null;
  docNumber?: string | null;
  quotedAt?: string | null;
}

/**
 * True for a lead time from the item master (13 Oct 2026, behind `RFQ_LEAD_ITEM_MASTER`):
 * the page shows it as today's figure with its source, the reply says "about, to be
 * confirmed".
 */
export function isItemMasterLead(source: LeadTimeSourceFacts | null | undefined): boolean {
  return (
    source?.kind === "item_master_sales" ||
    source?.kind === "item_master_purchase" ||
    source?.kind === "item_master_inventory"
  );
}

/** The lead time status of a line (P-02), as the line block reads it. */
export type LeadTimeStatusFacts = "not_looked_up" | "none_in_fo" | "current" | "historic";

/** True for a lead time from an earlier quotation or mail offer: context, never a promise. */
export function isHistoricLead(
  source: LeadTimeSourceFacts | null | undefined,
  status?: LeadTimeStatusFacts | null,
): boolean {
  return status === "historic" || source?.kind === "last_quoted" || source?.kind === "mail_offer";
}

/**
 * "From quotation SQ0990123 on 1 Sep 2026" or "From a mail offer on 2 Sep 2026": where a
 * historic lead time comes from (P-02), so it never reads as today's lead time.
 */
export function leadOriginText(source: LeadTimeSourceFacts | null | undefined): string | null {
  if (!source) {
    return null;
  }
  const day = source.quotedAt ? formatDate(source.quotedAt) : null;
  if (source.kind === "last_quoted") {
    const id = source.docNumber?.trim() || "";
    if (!id) {
      return day ? LEAD_ORIGIN_WORDS.quotationUnnamed(day) : LEAD_ORIGIN_WORDS.quotationUnknown;
    }
    return day ? LEAD_ORIGIN_WORDS.quotation(id, day) : LEAD_ORIGIN_WORDS.quotationUndated(id);
  }
  if (source.kind === "mail_offer") {
    return day ? LEAD_ORIGIN_WORDS.mailOffer(day) : LEAD_ORIGIN_WORDS.mailOfferUndated;
  }
  if (source.kind === "item_master_sales") {
    return LEAD_ORIGIN_WORDS.itemMasterSales;
  }
  if (source.kind === "item_master_purchase") {
    return LEAD_ORIGIN_WORDS.itemMasterPurchase;
  }
  if (source.kind === "item_master_inventory") {
    return LEAD_ORIGIN_WORDS.itemMasterInventory;
  }
  return null;
}

/**
 * The line without a lead time (DK manager review MD-04, 12 Oct 2026): plain words, no
 * source names. The reply draft says "lead time to confirm" on that line by itself.
 * Sales review of 12 Oct 2026 (P-02): "not looked up yet" is one state, "no lead time
 * in F&O" the other.
 */
export const LEAD_TIME_NOT_LOOKED_UP = "Lead time: not looked up yet";
export const LEAD_TIME_NONE_IN_FO: string = LEAD_GAP_WORDS.none_in_fo;

/**
 * The words of the lead cell without a lead time (P-02): "No lead time in F&O" when the
 * server says F&O was read and holds none, else "Not looked up yet".
 */
export function leadGapWords(status: LeadTimeStatusFacts | null | undefined): string {
  return status === "none_in_fo" ? LEAD_GAP_WORDS.none_in_fo : LEAD_GAP_WORDS.not_looked_up;
}

/**
 * "Lead time 14 days (price agreement)", "Lead time 23 weeks (from quotation SQ0123 on
 * 1 Sep 2026, context only)", else which gap it is (sim360 P09: never a bare gap).
 */
export function leadTimeSentence(
  days: number | null | undefined,
  source?: LeadTimeSourceFacts | null,
  status?: LeadTimeStatusFacts | null,
): string {
  if (typeof days !== "number" || !Number.isFinite(days)) {
    return status === "none_in_fo" ? LEAD_TIME_NONE_IN_FO : LEAD_TIME_NOT_LOOKED_UP;
  }
  const period = source?.text?.trim() || `${days} ${days === 1 ? "day" : "days"}`;
  if (isHistoricLead(source, status)) {
    const origin = leadOriginText(source) ?? LEAD_ORIGIN_WORDS.quotationUnknown;
    const where = origin.charAt(0).toLowerCase() + origin.slice(1);
    return `Lead time ${period} (${where}, context only)`;
  }
  if (source?.kind === "trade_agreement") {
    return `Lead time ${period} (price agreement)`;
  }
  if (source?.kind === "purchase") {
    return `Lead time ${period} (purchase lead time in IMDP, confirm the delivery date)`;
  }
  if (isItemMasterLead(source)) {
    return `Lead time ${period} (item master, about, to be confirmed)`;
  }
  return `Lead time ${period}`;
}

export type TagTone = "success" | "warning" | "neutral" | "info" | "danger";

const MARGIN: Record<MarginStatus, { label: string; tone: TagTone }> = {
  ok: { label: "Margin: OK", tone: "success" },
  needs_approval: { label: "Margin: needs approval", tone: "warning" },
  not_checked: { label: "Margin: not checked (no cost in F&O)", tone: "neutral" },
};

export function marginTag(
  status: string | null | undefined,
): { label: string; tone: TagTone } | null {
  return status && Object.hasOwn(MARGIN, status) ? MARGIN[status as MarginStatus] : null;
}

/** The item status tag; null for an item that is fine (or without a status). */
export function itemStatusTag(
  status: ItemStatus | string | null | undefined,
  replacement?: string | null,
): { label: string; tone: TagTone } | null {
  switch (status) {
    case "blocked":
      return { label: "Blocked for sale in F&O", tone: "danger" };
    case "phase_out":
      return {
        label: replacement ? `Phase-out item, replacement ${replacement}` : "Phase-out item",
        tone: "warning",
      };
    case "service":
      return { label: "Service item", tone: "neutral" };
    default:
      return null;
  }
}

// ─── order check ────────────────────────────────────────────────────────────

const VERDICT_TONE: Record<OrderVerdict, TagTone> = {
  same: "success",
  qty_differs: "warning",
  price_differs: "warning",
  qty_and_price_differ: "warning",
  partly_compared: "neutral",
  not_in_quotation: "warning",
  not_resolved: "neutral",
};

export function orderVerdictTone(verdict: string | null | undefined): TagTone {
  return verdict && Object.hasOwn(VERDICT_TONE, verdict)
    ? VERDICT_TONE[verdict as OrderVerdict]
    : "neutral";
}

/** The verdict as printed: the server's sentence, else the same wording built here. */
export function orderVerdictText(line: OrderCheckLine): string {
  if (typeof line.text === "string" && line.text.trim() !== "") {
    return line.text.trim();
  }
  const qty = `Ordered ${formatQty(line.ordered_qty)}, quoted ${formatQty(line.quoted_qty)}`;
  const price = `Order price ${formatAmount(line.ordered_price)}, quoted ${formatAmount(line.quoted_price)}`;
  switch (line.verdict) {
    case "same":
      return "As quoted";
    case "qty_differs":
      return qty;
    case "price_differs":
      return price;
    case "qty_and_price_differ":
      return `${qty}. ${price}`;
    case "partly_compared":
      return "Not fully compared";
    case "not_in_quotation":
      return "Not in the quotation";
    default:
      return "The item is not resolved, so there is no comparison";
  }
}

// ─── customer ───────────────────────────────────────────────────────────────

/** The stored rule name of a customer a person confirmed (`customer_resolution.how`). */
const HOW_REP_CONFIRMED = "rep-confirmed";

/**
 * True when a PERSON decided the customer of the case: a confirmation or a pick on this
 * case (`work.customer_account`), or the stored result of one (`confirmed`, or the rule
 * "rep-confirmed"). False when the customer is certain from I2Q's own evidence, and for
 * every customer that is not certain. The API already carries both facts, so the two
 * words of the line need no new field (sales review of 12 Oct 2026, C-06).
 */
export function customerDecidedBySales(
  detail: Pick<QuoteCaseDetail, "work" | "customerBlock">,
): boolean {
  const block = detail.customerBlock ?? null;
  return (
    Boolean(detail.work?.customer_account?.trim()) ||
    block?.confirmed === true ||
    block?.how === HOW_REP_CONFIRMED
  );
}

/**
 * The word beside a certain customer: "Confirmed" (I2Q is sure) or "Confirmed by sales"
 * (a person decided). One helper for the customer line, the summary strip and the
 * practice case, so the three never print different words (C-06).
 */
export function customerConfirmedLabel(
  detail: Pick<QuoteCaseDetail, "work" | "customerBlock">,
): string {
  return customerConfirmedWord(customerDecidedBySales(detail));
}

const ROLE_LABELS: Record<Exclude<CustomerRole, "unknown">, string> = {
  customer: "Customer",
  agent: "Agent",
  norican_colleague: "Norican colleague",
  portal: "Procurement portal",
  sister_company: "Sister company",
};

/** The queue's "Sender" filter and the badge on a row. A plain customer gets no badge. */
export const SENDER_OPTIONS: readonly { value: Exclude<CustomerRole, "unknown">; label: string }[] =
  [
    { value: "customer", label: "Customer" },
    { value: "sister_company", label: "Sister company" },
    { value: "norican_colleague", label: "Forwarded by a colleague" },
    { value: "agent", label: "Agent" },
    { value: "portal", label: "Portal" },
  ];

/** The badge of a queue row: null for a customer, an unknown role or no role. */
export function senderBadgeLabel(role: string | null | undefined): string | null {
  if (!role || role === "customer") {
    return null;
  }
  return SENDER_OPTIONS.find((option) => option.value === role)?.label ?? null;
}

const CANDIDATE_KIND_LABELS: Record<string, string> = {
  intercompany: "Intercompany account",
  invoice_family: "Same invoice account",
  quote_ref: "Account of the quotation named in the mail",
  ship_to: "Delivery postcode matches",
  redirect: "Replaces an account marked do not use",
  contact_memory: "Earlier documents for this sender went here",
  domain_memory: "Earlier documents for this mail domain went here",
  investigation: "Suggested by the deeper search, with its source",
  // Hunt round 2, decision 9: a suggestion, never one click on its own.
  alias: "Initials or F&O search name match",
  // Hunt round 3, Q-16: twin master rows. I2Q proposes none; a person picks one.
  twin: "Same name and address",
};

/**
 * The aria label of "Use this" on a candidate account. A twin (hunt round 3, Q-16) has the
 * same name as another account, so its label keeps the account number.
 */
export function candidateUseLabel(candidate: {
  account: string;
  name?: string | null;
  kind?: string | null;
}): string {
  const name = candidate.name?.trim();
  if (!name) {
    return `Use ${candidate.account} as the customer`;
  }
  return candidate.kind === "twin"
    ? `Use ${name} (${candidate.account}) as the customer`
    : `Use ${name} as the customer`;
}

/** Why an account is offered (the stage 3 and 4 kinds); null for the older kinds. */
export function candidateKindLabel(kind: string | null | undefined): string | null {
  return kind && Object.hasOwn(CANDIDATE_KIND_LABELS, kind)
    ? (CANDIDATE_KIND_LABELS[kind] as string)
    : null;
}

/**
 * Stage 4: how I2Q settled the customer, as a short tag. Values ending in "?"
 * are suggestions only. Null for any other `how` (no tag is printed).
 */
const HOW_LABELS: Record<string, string> = {
  // the first rules (review of 11 Oct 2026, F04: every account choice says what it rests on)
  "exact-email": "Sender's address known",
  "forwarded-contact": "Forwarded sender's address known",
  history: "Earlier quotations of the sender",
  "content-recover": "Company named in the mail",
  "attachment-issuer": "Issuer of the attached order",
  "portal-buyer": "Buyer named by the portal",
  "sibling-choice": "One of several accounts of the firm",
  "agent-end-customer": "End customer named by the agent or trader",
  "agent-end-customer-open": "End customer named, no account found",
  "agent-own-account": "The agent's own account",
  // intercompany-order has its own line on the card (the sister company and its proof)
  "contact-memory": "Known sender",
  "intercompany-memory": "Known sister company",
  "quote-ref": "Quotation named in the mail",
  // Hunt round 2 (Q-18): the account number the mail writes. A suggestion.
  "account-ref": "Account number written in the mail",
  "contact-memory?": "Earlier documents of the sender",
  "domain-memory?": "Earlier documents of the domain",
  "agent-memory?": "Earlier documents of the agent",
  "ship-to": "Delivery address",
  // stage 5
  "evidence-families": "Independent signs agree",
  "sibling-postcode?": "Site named by postcode",
  "sibling-docnum?": "Site named by a number",
  // re-read of 13 Oct 2026 (point 4): the mail names the city of one account of the firm
  "sibling-city?": "Site named by its city",
  "sibling-certain": "Known sender at this site",
  "alias-memory": "End customer booked by sales before",
  "alias-memory?": "End customer booked by sales once",
  "confirmed-proposal": "Booked by sales again and again",
  // Hunt round 3, Q-16: twin master rows, no proposal (`HOW_TWINS` in quote_agent.py).
  "twin-accounts": "Same name and address",
  "ranked?": "Most evidence in the mail",
};

export function customerHowLabel(how: string | null | undefined): string | null {
  return how && Object.hasOwn(HOW_LABELS, how) ? (HOW_LABELS[how] as string) : null;
}

const EVIDENCE_KIND_LABELS: Record<string, string> = {
  ambiguity: "Please check",
  rule: "Why I2Q proposed it",
  name_read: "In the mail",
  contact_memory: "Earlier documents",
  intercompany_memory: "Earlier intercompany orders",
  evidence_family: "Independent sign",
  sibling_choice: "Which site",
  sales_confirmation: "Booked by sales",
  ranking: "Evidence",
  // decision D5 (20 Oct 2026): the sender buys for more than one customer account
  multi_account: "Several accounts",
};

/** The tag in front of an evidence sentence; null for the kinds without one. */
export function evidenceKindLabel(kind: string | null | undefined): string | null {
  return kind && Object.hasOwn(EVIDENCE_KIND_LABELS, kind)
    ? (EVIDENCE_KIND_LABELS[kind] as string)
    : null;
}

const PROOF_LABELS: Record<string, string> = {
  "exchange-internal": "Internal mail",
  "trusted-relay": "Sent through the Norican relay",
};

/** Stage 4: what proves an intercompany mail is from inside Norican; null when not proven. */
export function intercompanyProofLabel(proof: string | null | undefined): string | null {
  return proof && Object.hasOwn(PROOF_LABELS, proof) ? (PROOF_LABELS[proof] as string) : null;
}

// ─── prepare quotation ──────────────────────────────────────────────────────

/** Shown next to the disabled create button when the server sent no note. */
export const FO_WRITE_NOTE = "Writing quotations to F&O is switched off. Nothing is sent.";

const LEFT_OUT_REASONS: Record<string, string> = {
  no_item_decided: "Marked as no item",
  undecided: "Not decided yet",
  failed: "Not evaluated. Check it by hand",
};

/** Why a line is not part of the prepared quotation. */
export function leftOutReasonLabel(reason: string | null | undefined): string {
  return reason && Object.hasOwn(LEFT_OUT_REASONS, reason)
    ? (LEFT_OUT_REASONS[reason] as string)
    : "Not included";
}

/** The role of the sender as a label; null for `unknown` or no role. */
export function customerRoleLabel(role: string | null | undefined): string | null {
  return role && Object.hasOwn(ROLE_LABELS, role)
    ? ROLE_LABELS[role as keyof typeof ROLE_LABELS]
    : null;
}

// ─── line decisions ─────────────────────────────────────────────────────────

/** Who decided a line I2Q decided by the exact item number (Timo, 10 Oct 2026). */
export const I2Q_EXACT_DECIDER = "I2Q (exact number)";
/** The sentence of such a line. */
export const I2Q_EXACT_DECIDED = "Decided by I2Q (exact number)";
/** Under a line I2Q decided: what it means and how to take it back. */
export const I2Q_EXACT_NOTE =
  "The customer's item number matched exactly (certified for this area). Undo to decide the line yourself.";

/**
 * Decision D7 (19 Oct 2026): the quiet warning on an item of "Pick item" whose name shares
 * no word with the line (`itemNameDiffers` in `features/case-review/item-name-match.ts`).
 * The pick still saves.
 */
export function pickNameDiffersText(lineText: string): string {
  return `The item name differs from the line: ${lineText.trim()}`;
}

/**
 * The "Earlier decisions" card (finding 14 of the hunt review, round 3): a read of the mail
 * again keeps a decision on its line when the item, a part number or the position matches
 * (`rekey_rows`). A decision that matches no line stays on the case under this label.
 */
export const ORPHAN_DECISION_WORDS = {
  /** Above the line text of each decision without a line. */
  label: "Your decision on a line that I2Q no longer reads",
  caption:
    "I2Q read the mail again and found no line for these decisions. These lines are no longer in the draft.",
} as const;

/** "Confirmed by Anna Jensen on 07 Oct 2026" / "Picked by ..." / "Marked as no item by ...". */
export function decisionSentence(
  decision: Pick<LineDecision, "kind" | "decided_by" | "decided_at" | "by_i2q">,
  nameOf: (email: string) => string,
): string {
  if (decision.by_i2q) {
    return I2Q_EXACT_DECIDED;
  }
  const verb =
    decision.kind === "confirm"
      ? "Confirmed"
      : decision.kind === "item"
        ? "Picked"
        : "Marked as no item";
  const who = nameOf(decision.decided_by) || decision.decided_by;
  const on = decision.decided_at ? ` on ${formatDay(decision.decided_at)}` : "";
  return `${verb} by ${who}${on}`;
}

/**
 * The first mail's decision on a twin's line (sim360 P18): "Same inquiry RFQ-x: Picked
 * 4-0104-099 by Ann on 08 Oct 2026". A suggestion only; nothing is stored on the twin.
 */
export function twinDecisionSentence(
  twin: Pick<LineDecision, "kind" | "item" | "decided_by" | "decided_at" | "by_i2q"> & {
    case_id: string;
  },
  nameOf: (email: string) => string,
): string {
  const what = decisionSentence(twin, nameOf);
  const item = twin.kind !== "no_item" && twin.item ? ` ${twin.item}` : "";
  const [verb, ...rest] = what.split(" by ");
  const tail = rest.length > 0 ? ` by ${rest.join(" by ")}` : "";
  return `Same inquiry ${twin.case_id}: ${verb}${item}${tail}`;
}

/**
 * The decision request that takes over the first mail's decision on a twin's line: the
 * same item (a plain confirm when the twin's I2Q proposes that item too), or no item.
 */
export function twinDecisionRequest(
  twin: Pick<LineDecision, "kind" | "item" | "item_area">,
  matchedItemId: string | null | undefined,
): { kind: "confirm" | "item" | "no_item"; item?: string; item_area?: string } {
  if (twin.kind === "no_item") {
    return { kind: "no_item" };
  }
  const item = twin.item?.trim();
  if (!item || (matchedItemId && matchedItemId.trim().toUpperCase() === item.toUpperCase())) {
    return { kind: "confirm" };
  }
  return twin.item_area
    ? { kind: "item", item, item_area: twin.item_area }
    : { kind: "item", item };
}

/** Shown on a line whose item was found by its number while the customer is still open. */
export const CUSTOMER_OPEN_SENTENCE =
  "Item found by its number. Confirm the customer to finish the check.";
/** A `customer_open` line whose item is a guess from the description, not a number. */
export const CUSTOMER_OPEN_GUESS_SENTENCE =
  "Best guess from the description, no number matched. Confirm the customer and the item.";
/** A `customer_open` line whose item was read from a damaged number. */
export const CUSTOMER_OPEN_REPAIRED_SENTENCE =
  "Read from a damaged number. Confirm the customer and check the item.";
/** A `customer_open` line without any item. */
export const CUSTOMER_OPEN_NO_ITEM_SENTENCE = "No item found. Pick one or mark No item.";

/** The ways of finding an item that really are "by its number". */
const BY_NUMBER_BASES = new Set(["exact_number", "alias", "supersession"]);

/**
 * The sentence of a line held while the customer is open, from how the item was found:
 * only a number, an alias or a successor is "found by its number" (360 simulation P05).
 * A line from an older payload without `match_basis` keeps the number sentence when it
 * has an item.
 */
/** The customer is still to confirm on this line: by its number or by a search (P05). */
export function isCustomerOpen(reasonCode: string | null | undefined): boolean {
  return reasonCode === "customer_open" || reasonCode === "customer_open_search";
}

export function customerOpenSentence(line: {
  matchedItemId: string | null;
  matchBasis?: string | null;
  reasonCode?: string | null;
}): string {
  if (!line.matchedItemId) {
    return CUSTOMER_OPEN_NO_ITEM_SENTENCE;
  }
  const basis = line.matchBasis ?? null;
  // A search's pick is never "found by its number", whatever basis an older payload sent.
  if (line.reasonCode === "customer_open_search" && (basis == null || BY_NUMBER_BASES.has(basis))) {
    return CUSTOMER_OPEN_GUESS_SENTENCE;
  }
  if (basis == null || BY_NUMBER_BASES.has(basis)) {
    return CUSTOMER_OPEN_SENTENCE;
  }
  return basis === "repaired_number"
    ? CUSTOMER_OPEN_REPAIRED_SENTENCE
    : CUSTOMER_OPEN_GUESS_SENTENCE;
}

// ─── the deeper search (round 2, 13 Oct 2026) ───────────────────────────────

/**
 * One sentence for each thing a mail can do with one of our quotations. The keys are the
 * kinds of the revision pass (`RevisionAnswer.kind` in
 * `backend/src/rfq_foundry/matching/investigation/revision.py`); `none` is never sent.
 * `order` is the kind of the follow-up (`QuoteFollowUp.kind`, decision 1 of 14 Oct 2026):
 * an order or a signed copy of our quotation.
 */
export const REVISION_SENTENCES: Record<string, (quotation: string) => string> = {
  amend: (quotation) => `The mail changes our quotation ${quotation}.`,
  order: (quotation) => `The mail orders on our quotation ${quotation}.`,
  requote: (quotation) => `The mail asks for our quotation ${quotation} again.`,
  question: (quotation) => `The mail asks a question about our quotation ${quotation}.`,
};

/**
 * The words of the deeper search on the case page: the proposal on the customer line and
 * on a row, the "Why" panel, "Look again" and the provisional values. The buttons are in
 * `ACTION_LABELS` ("Confirm", "Not this one", "Look again", "Why"). One word for one thing:
 * the search is "the deeper search" on every screen, in Help and in the training.
 */
export const INVESTIGATION_WORDS = {
  /** The tag on a proposed account or item. */
  proposed: "Proposed by the deeper search",
  /** The heading of the panel behind "Why" on a proposal. */
  whyHeading: "Why this proposal",
  /** Over the lookups of the panel. */
  lookups: "What the deeper search looked up",
  /** The mark on the lookup that decided. */
  decided: "This lookup decided",
  /** A lookup without a row to show. */
  noRows: "No row to show.",
  /** The badge and the status line while a request waits for I2Q. */
  lookingAgain: "Looking again",
  lookingAgainNote: "The deeper search looks at this case again. The result comes on this page.",
  /** The toast after the request was stored. */
  lookAgainAsked: "The deeper search looks at the case again.",
  /** The chip of a provisional value. */
  provisional: "Provisional",
  /** The second account of an agent pair, and any other account next to the proposal. */
  otherAccount: "Other account",
  /** The tag of an item shown beside the item of the line. */
  alternative: "Alternative",
  alternativeNote: "The line keeps its item. Pick this one to change it.",
  /** A proposal with a doubt is a candidate, never the first choice. */
  doubtLead: "Check this before you confirm",
  confirmed: "You confirmed this proposal.",
  rejected: "You rejected this proposal.",
  /** The toast after "Not this one". */
  rejectedToast: "Proposal rejected",
  rejectedNote: "The proposal stays in the history of the case.",
  /** "Look again" on one row: the aria label names the line. */
  lookAgainLine: (asked: string) => `Look again at the line: ${asked}`,
  /**
   * What the mail does with one of our quotations, over the draft. `kind` is the word of
   * the revision pass (`revision.kind` of the wire): `amend`, `requote` or `question`.
   * An unknown word gets the neutral sentence.
   */
  revision: (quotation: string, kind: string) =>
    Object.hasOwn(REVISION_SENTENCES, kind)
      ? (REVISION_SENTENCES[kind] as (quotation: string) => string)(quotation)
      : `The mail names our quotation ${quotation}.`,
} as const;

/** "Confirm Acme Foundry (700123)": the button of a proposed account. */
export function proposalConfirmLabel(name: string, account: string): string {
  return name ? `Confirm ${name} (${account})` : `Confirm ${account}`;
}

/** The sentence under a provisional price. It names the account and the price group. */
export function provisionalPriceSentence(account: string, group: string | null): string {
  const where = group
    ? `List price for account ${account}, price group ${group}.`
    : `List price for account ${account}.`;
  return `${where} Confirm the customer to get the price.`;
}

/** "Price for quantity 1. The mail gives no quantity." */
export const PROVISIONAL_QTY_ASSUMED = "Price for quantity 1. The mail gives no quantity.";

/** "Usual discount of the account: 5 %" or "Last discount of this item for the account: 5 %". */
export function provisionalDiscountText(
  pct: string | null,
  basis: "item_last" | "account_usual" | null,
): string | null {
  if (!pct || !basis) {
    return null;
  }
  const value = formatAmount(pct).replace(/\.00$/, "");
  return basis === "item_last"
    ? `Last discount of this item for the account: ${value} %`
    : `Usual discount of the account: ${value} %`;
}

/** "Last quoted to this account: 39.00 GBP on 02 Jun 2026". */
export function provisionalLastPriceText(
  price: string | null,
  currency: string | null,
  day: string | null,
): string | null {
  if (!price) {
    return null;
  }
  const amount = [formatAmount(price), currency].filter(Boolean).join(" ");
  return day
    ? `Last quoted to this account: ${amount} on ${formatDay(day)}`
    : `Last quoted to this account: ${amount}`;
}

/** Where a provisional lead time comes from (`lead_source` of the wire). */
const PROVISIONAL_LEAD_SOURCES: Record<string, string> = {
  price_row: "the trade agreement",
  last_quoted: "the last sent quotation of the item",
  last_offered: "the last offer of the item",
  purchase_lead: "the purchase lead time",
  item_master_sales: "the item master, sales lead time",
  item_master_purchase: "the item master, purchase lead time",
  item_master_inventory: "the item master, inventory lead time",
};

/** "Lead time 21 days, from the item master, sales lead time. Context only." */
export function provisionalLeadText(days: number | null, source: string | null): string | null {
  if (days == null) {
    return null;
  }
  const period = `${days} ${days === 1 ? "day" : "days"}`;
  const from = source ? PROVISIONAL_LEAD_SOURCES[source] : undefined;
  return from
    ? `Lead time ${period}, from ${from}. Context only.`
    : `Lead time ${period}. Context only.`;
}

/**
 * The label of record of each lookup of the deeper search (the server sends the name of
 * the lookup, never a sentence). An unknown name reads "Lookup".
 */
export const LOOKUP_LABELS: Record<string, string> = {
  account_search: "Customer search",
  account_details: "Customer master data",
  account_of_document: "Our document number",
  account_activity: "Quotations of the account in 12 months",
  quotation_lines: "Lines of our quotation",
  item_lookup: "Item number in the catalogue",
  item_near_miss: "Similar item numbers",
  item_by_customer_number: "Item number of the customer",
  item_search_text: "Search in the item names",
  replacement_chain: "Replacement items",
  price_for: "List price",
  last_prices: "Last quoted prices",
  lead_time: "Lead time",
  usual_discount: "Usual discount",
  bom: "Bill of materials of the machine",
};

export function lookupLabel(lookup: string): string {
  return Object.hasOwn(LOOKUP_LABELS, lookup) ? (LOOKUP_LABELS[lookup] as string) : "Lookup";
}

/** What supports a proposed account, as a short tag (`signals` of the wire). */
export const SIGNAL_LABELS: Record<string, string> = {
  document: "Our document number",
  mail_sentence: "Named in the mail",
  account_number: "Account number in the mail",
  contact: "Known contact",
  domain: "Known mail domain",
  place: "Same place",
  history: "Earlier quotations",
  name: "Name only",
};

/** The tag of a signal; null for a signal this version does not know. */
export function signalLabel(signal: string): string | null {
  return Object.hasOwn(SIGNAL_LABELS, signal) ? (SIGNAL_LABELS[signal] as string) : null;
}
